import * as React from 'react';
import { useRef, useState } from 'react';
import { Dialog, Field, SectionCard, Select, TextInput } from '../../../components/forms';
import { Segmented } from '../../../components/table';
import {
    ASSIGNMENT_STATUSES,
    AsyncConfirm,
    firstName,
    fullName,
    label,
    ProductionApi,
    rateLabel,
    reason,
    RemoveButton,
    RowSelect,
    STAFF_ROLES,
    useInflight,
} from './parts';

/*
 * Crew assignments — each add, status change and removal saves on its own.
 * Adding someone makes the server email them a gig offer (and, on their
 * first gig, a crew-account invite), so the add ends on a confirm step.
 */

interface AddForm {
    staffId: string;
    role: string;
    rate: string;
    rateType: 'hourly' | 'flat';
}
const BLANK: AddForm = { staffId: '', role: 'photographer', rate: '', rateType: 'hourly' };

export default function CrewSection({
    api,
    activationId,
    eventName,
    crew,
    staff,
    staffFailed,
    refresh,
    toast,
}: {
    api: ProductionApi;
    activationId: number;
    eventName: string;
    crew: any[];
    staff: any[];
    staffFailed: boolean;
    refresh: () => Promise<void>;
    toast: (text: string) => void;
}) {
    const { busy, run } = useInflight();
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState<Record<number, string>>({});

    const [adding, setAdding] = useState(false);
    const [step, setStep] = useState<'form' | 'confirm'>('form');
    const [form, setForm] = useState<AddForm>(BLANK);
    const [addError, setAddError] = useState<string | null>(null);
    /* when the confirm step appeared: a double-click on Review must not land on Assign */
    const shownAt = useRef(0);

    const [removing, setRemoving] = useState<any | null>(null);
    const [removeError, setRemoveError] = useState<string | null>(null);

    const available = staff
        .filter((s) => !crew.some((a) => +a.staffId === +s.id))
        .sort((a, b) => fullName(a).localeCompare(fullName(b)));
    const person = staff.find((s) => +s.id === +form.staffId) ?? null;
    const rateOk = form.rate === '' || (Number.isFinite(+form.rate) && +form.rate >= 0);

    const openAdd = () => {
        setForm(BLANK);
        setStep('form');
        setAddError(null);
        setAdding(true);
    };
    const closeAdd = () => {
        if (busy.add) return;
        setAdding(false);
    };

    const review = () => {
        if (!person || !rateOk) return;
        shownAt.current = Date.now();
        setStep('confirm');
    };

    const assign = async () => {
        if (!person || Date.now() - shownAt.current < 600) return;
        setAddError(null);
        /* same body as v2 */
        const r = await run('add', () =>
            api.addStaffAssignment(activationId, {
                staffId: +form.staffId,
                role: form.role,
                rate: form.rate === '' ? undefined : +form.rate,
                rateType: form.rateType,
            }),
        );
        if (r.ok) {
            setAdding(false);
            await refresh();
            toast(person.email ? `${fullName(person)} added — a gig offer is on its way to ${person.email}.` : `${fullName(person)} added to the crew.`);
        } else if (!('skipped' in r && r.skipped)) {
            setAddError(reason(r.error, `Couldn’t add ${fullName(person)} — nothing was sent. Try again.`));
        }
    };

    const setStatus = async (a: any, status: string) => {
        if (status === a.status) return;
        setError(null);
        setPending((p) => ({ ...p, [a.id]: status }));
        const r = await run(`status-${a.id}`, () => api.setStaffAssignmentStatus(+a.id, status));
        if (r.ok) await refresh();
        else if (!('skipped' in r && r.skipped)) setError(reason(r.error, `Couldn’t change ${fullName(a)} to ${label(status).toLowerCase()} — it’s back to ${label(a.status).toLowerCase()}.`));
        setPending((p) => {
            const next = { ...p };
            delete next[a.id];
            return next;
        });
    };

    const remove = async () => {
        if (!removing) return;
        setRemoveError(null);
        const a = removing;
        const r = await run(`remove-${a.id}`, () => api.removeStaffAssignment(+a.id));
        if (r.ok) {
            setRemoving(null);
            await refresh();
            toast(`${fullName(a)} removed from the crew.`);
        } else if (!('skipped' in r && r.skipped)) {
            setRemoveError(reason(r.error, `Couldn’t remove ${fullName(a)} — try again.`));
        }
    };

    const firstGig = person && !person.accountId && !person.inviteSentAt;

    return (
        <SectionCard
            title="Crew"
            note={crew.length ? String(crew.length) : undefined}
            action={
                <button type="button" className="tb-btn sm" onClick={openAdd} disabled={staffFailed}>
                    Add crew
                </button>
            }
        >
            {staffFailed && <p className="field-e">Couldn’t load the staff roster, so crew can’t be added. Reload to try again.</p>}
            {crew.length === 0 ? (
                <p className="quiet-empty">No crew assigned yet.</p>
            ) : (
                crew.map((a) => {
                    const st = pending[a.id] ?? a.status;
                    return (
                        <div className="trow2 prd-row" key={a.id}>
                            <span className="prd-main">
                                <span className="nm2">{fullName(a)}</span>
                                <span className="sub2">
                                    {[label(a.role), rateLabel(a.rate, a.rateType), a.email].filter(Boolean).join(' · ')}
                                </span>
                            </span>
                            <span className="prd-ctl">
                                <RowSelect
                                    value={st}
                                    options={ASSIGNMENT_STATUSES}
                                    disabled={!!busy[`status-${a.id}`] || !!busy[`remove-${a.id}`]}
                                    ariaLabel={`${fullName(a)} response`}
                                    onChange={(v) => setStatus(a, v)}
                                />
                                <RemoveButton
                                    label={`Remove ${fullName(a)} from the crew`}
                                    disabled={!!busy[`remove-${a.id}`]}
                                    onClick={() => {
                                        setRemoveError(null);
                                        setRemoving(a);
                                    }}
                                />
                            </span>
                        </div>
                    );
                })
            )}
            {error && (
                <p className="field-e" role="alert">
                    {error}
                </p>
            )}

            <Dialog
                open={adding}
                title={step === 'form' ? 'Add crew' : `Assign ${person ? fullName(person) : ''}?`}
                onClose={closeAdd}
                onSubmit={() => {
                    /* Enter only advances; sending needs a deliberate click */
                    if (step === 'form') review();
                }}
                footer={
                    step === 'form' ? (
                        <>
                            <button type="button" className="tb-btn" onClick={closeAdd}>
                                Cancel
                            </button>
                            <button type="submit" className="tb-new" disabled={!person || !rateOk}>
                                Review
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="tb-btn" onClick={() => setStep('form')} disabled={!!busy.add}>
                                Back
                            </button>
                            <button type="button" className="tb-new" disabled={!!busy.add} onClick={assign}>
                                {busy.add ? 'Assigning…' : person?.email ? `Assign & email ${firstName(person)}` : 'Assign'}
                            </button>
                        </>
                    )
                }
            >
                {step === 'form' ? (
                    <>
                        <Field label="Person">
                            <Select autoFocus value={form.staffId} onChange={(e) => setForm({ ...form, staffId: e.target.value })}>
                                <option value="">Choose someone…</option>
                                {available.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {fullName(s)}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field label="Role">
                            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                                {STAFF_ROLES.map((r) => (
                                    <option key={r} value={r}>
                                        {label(r)}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <div className="field">
                            <span className="field-l">Rate — optional</span>
                            <span className="prd-rate">
                                <TextInput
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    placeholder="0"
                                    aria-label="Rate in dollars"
                                    aria-invalid={rateOk ? undefined : true}
                                    value={form.rate}
                                    onChange={(e) => setForm({ ...form, rate: e.target.value })}
                                />
                                <Segmented<'hourly' | 'flat'>
                                    label="Rate type"
                                    value={form.rateType}
                                    onChange={(v) => setForm({ ...form, rateType: v })}
                                    options={[
                                        { value: 'hourly', label: 'Per hour' },
                                        { value: 'flat', label: 'Flat' },
                                    ]}
                                />
                            </span>
                            {!rateOk && <span className="field-e">Use a positive number.</span>}
                        </div>
                        {available.length === 0 && <p className="field-h">Everyone on the roster is already on this crew.</p>}
                    </>
                ) : (
                    person && (
                        <div className="prd-confirm">
                            <p>
                                <b>{fullName(person)}</b> joins the crew for {eventName} as {label(form.role).toLowerCase()}
                                {rateLabel(form.rate, form.rateType) ? ` at ${rateLabel(form.rate, form.rateType)}` : ''}.
                            </p>
                            {person.email ? (
                                <p className="prd-notice">
                                    <span className="prd-dot" aria-hidden="true" />
                                    <span>
                                        This emails {firstName(person)} a gig offer at <b>{person.email}</b> as soon as you assign
                                        {firstGig ? ', plus a one-time invite to set up a crew account' : ''}. The email can’t be recalled.
                                    </span>
                                </p>
                            ) : (
                                <p className="field-h">{firstName(person)} has no email on file, so no offer is sent.</p>
                            )}
                            {addError && (
                                <p className="field-e" role="alert">
                                    {addError}
                                </p>
                            )}
                        </div>
                    )
                )}
            </Dialog>

            <AsyncConfirm
                open={!!removing}
                title={`Remove ${removing ? fullName(removing) : ''} from the crew?`}
                actionLabel="Remove"
                busyLabel="Removing…"
                busy={!!(removing && busy[`remove-${removing.id}`])}
                error={removeError}
                danger
                onConfirm={remove}
                onClose={() => setRemoving(null)}
            >
                <p>
                    Their assignment and its {removing ? label(removing.status).toLowerCase() : ''} response are deleted. They aren’t emailed about it.
                </p>
            </AsyncConfirm>
        </SectionCard>
    );
}
